import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Ellipsis, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import ACL_RELATIONSHIPS from "acl-relationships";
import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Authorized from "components/Authorized";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import { invalidateEmailSignatureTableQuery } from "hooks/useEmailSignatureTable";
import EmailSignatureService from "services/EmailSignatures";

const EDIT_ACLS = [ACL_RELATIONSHIPS.emailSignatures.edit];
const DELETE_ACLS = [ACL_RELATIONSHIPS.emailSignatures.delete];
const CLONE_ACLS = [ACL_RELATIONSHIPS.emailSignatures.create];
const ANY_ACLS = [...EDIT_ACLS, ...DELETE_ACLS, ...CLONE_ACLS];

export const EmailSignatureTableActions = ({ original }) => {
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await EmailSignatureService.deleteEmailSignature(original.id);
      await invalidateEmailSignatureTableQuery();
      toast.success("Email signature deleted");
      setDeleteOpen(false);
    } catch {
      toast.error("Failed to delete email signature");
    }
  };

  return (
    <Authorized permissions={ANY_ACLS} partialMatch>
      <>
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label="Email signature actions"
            className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
          >
            <Ellipsis />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="tw:w-auto">
            <Authorized permissions={EDIT_ACLS}>
              <DropdownMenuItem
                onClick={() =>
                  navigate(`/settings/email_signatures/${original.id}`)
                }
              >
                <Pencil />
                Edit
              </DropdownMenuItem>
            </Authorized>
            <Authorized permissions={CLONE_ACLS}>
              <DropdownMenuItem
                onClick={() =>
                  navigate(
                    `/settings/email_signatures/new?clone=${original.id}`
                  )
                }
              >
                <Copy />
                Clone
              </DropdownMenuItem>
            </Authorized>
            <Authorized permissions={DELETE_ACLS}>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 />
                Delete
              </DropdownMenuItem>
            </Authorized>
          </DropdownMenuContent>
        </DropdownMenu>
        <Authorized permissions={DELETE_ACLS}>
          <ConfirmDeleteDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            title="Delete email signature?"
            description={
              original.name
                ? `Are you sure you want to delete the email signature "${original.name}"?`
                : "Are you sure you want to delete this email signature?"
            }
            onConfirm={handleDelete}
          />
        </Authorized>
      </>
    </Authorized>
  );
};
